import React, { useEffect, useState } from "react";
import { Button, Col, Form, Input, message, Modal, Row, Select } from "antd";
import { useTranslation } from "react-i18next";
import { COMPLAINT_REASONS, DEPARTMENTS } from "../../../../../constants/DepartmentConstants";

const { Option } = Select;
const { TextArea } = Input;

const ComplaintModal = ({
  visible,
  onClose,
  onSave,
  product, // Single product or { isBulk: true } for bulk mode
  existingComplaint, // Existing complaint data for editing
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const isBulkMode = product?.isBulk === true;

  // Watch form values to detect when "other" is selected
  const reasonValue = Form.useWatch("reason", form);

  // Pre-populate form with existing complaint data
  useEffect(() => {
    if (visible && existingComplaint && !isBulkMode) {
      form.setFieldsValue({
        reason: existingComplaint.reason,
        customReason: existingComplaint.customReason,
        department: existingComplaint.department,
        complainingPerson: existingComplaint.complainingPerson,
      });
    } else if (visible) {
      form.resetFields();
    }
  }, [visible, existingComplaint, form, isBulkMode]);

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Create complaint data for this specific product
      const complaintData = {
        productId: product.productId,
        productName: product.productName,
        reason: values.reason,
        customReason: values.reason === "other" ? values.customReason : null,
        department: values.department,
        complainingPerson: values.complainingPerson,
      };

      // Call onSave callback with complaint data
      onSave(complaintData);

      message.success(t("notifications.complaint_saved_successfully"));
      handleCancel();
    } catch (error) {
      console.error("Error in complaint submission:", error);
      message.error(t("notifications.error_submitting_complaint"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        title={isBulkMode ? t("apply_complaint_to_all_products") : t("complaint_modal_title")}
        open={visible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            {t("cancel")}
          </Button>,
          <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
            {isBulkMode ? t("apply_to_all") : t("save_complaint")}
          </Button>,
        ]}
        width={600}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          {isBulkMode && (
            <div
              style={{
                backgroundColor: "#e6f7ff",
                border: "1px solid #91d5ff",
                borderRadius: "6px",
                padding: "12px 16px",
                marginBottom: "16px",
              }}
            >
              <p style={{ margin: 0, color: "#666" }}>{t("bulk_complaint_info")}</p>
            </div>
          )}

          {/* Product Info - only show for single product */}
          {!isBulkMode && (
            <Row gutter={16}>
              <Col span={24}>
                <div style={{ marginBottom: 16, padding: 12, backgroundColor: "#f5f5f5", borderRadius: 6 }}>
                  <h4 style={{ margin: 0 }}>{product?.productName}</h4>
                  <p style={{ margin: 0, color: "#666", fontSize: "12px" }}>
                    {t("product_id")}: {product?.productId}
                  </p>
                </div>
              </Col>
            </Row>
          )}

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="reason"
                label={t("complaint_reason")}
                rules={[{ required: true, message: t("select_reason") }]}
              >
                <Select placeholder={t("select_reason")}>
                  {COMPLAINT_REASONS.map((reason) => (
                    <Option key={reason.id} value={reason.id}>
                      {t(reason.labelKey)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {/* Show custom reason input when "other" is selected */}
              {reasonValue === "other" && (
                <Form.Item
                  name="customReason"
                  label={t("specify_custom_reason")}
                  rules={[{ required: true, message: t("enter_custom_reason") }]}
                >
                  <Input placeholder={t("enter_custom_reason")} />
                </Form.Item>
              )}
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="department"
                label={t("department_error")}
                rules={[{ required: true, message: t("select_at_least_one_department") }]}
              >
                <Select placeholder={t("select_department")} allowClear>
                  {DEPARTMENTS.map((dept) => (
                    <Option key={dept.id} value={dept.id}>
                      {t(dept.nameKey)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="complainingPerson"
                label={t("complaining_person")}
                rules={[{ required: true, message: t("enter_complaining_person_name") }]}
              >
                <Input placeholder={t("enter_complaining_person_name")} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default ComplaintModal;
